import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, Check } from "lucide-react";
import { GOLD, NAVY } from "./ui";
import { NAV } from "../../data/business/profile";

const BUSINESS_LOGO =
  "https://customer-assets.emergentagent.com/job_b2ca0c7b-d03f-4a16-bd13-4c17923effbc/artifacts/x879m1f6_9.png";

const BusinessHeader = ({ lang, setLang, languages = [] }) => {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);

  const current = languages.find((l) => l.code === lang) || languages[0];
  const isActive = (item) =>
    item.to ? pathname === item.to : (item.children || []).some((c) => pathname === c.to);

  const go = () => { setMobileOpen(false); setOpenMenu(null); };

  return (
    <header className="bg-white/90 backdrop-blur-md border-b border-slate-200/70 sticky top-0 z-50" data-testid="business-header">
      <a href="#indhold" className="votra-skip-link" data-testid="biz-skip-to-content">Spring til indhold</a>
      <div className="h-[70px] md:h-[84px] px-4 sm:px-6 lg:px-12 flex items-center justify-between max-w-[1680px] mx-auto">
        <Link to="/business" className="flex items-center flex-shrink-0" onClick={go} aria-label="Votra Business forside">
          <img src={BUSINESS_LOGO} alt="Votra Business" width="224" height="56" className="h-[42px] md:h-[56px] w-auto object-contain" data-testid="business-logo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Votra Business hovedmenu">
          {NAV.map((item) => (
            <div key={item.label} className="relative"
                 onMouseEnter={() => item.children && setOpenMenu(item.label)}
                 onMouseLeave={() => item.children && setOpenMenu(null)}>
              {item.to ? (
                <Link
                  to={item.to}
                  onClick={go}
                  aria-current={isActive(item) ? "page" : undefined}
                  className="relative flex items-center gap-1 px-3.5 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
                  data-testid={`biz-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {item.label}
                  <span className="absolute bottom-0.5 left-3.5 right-3.5 h-[1.5px] origin-left transition-transform duration-300"
                        style={{ backgroundColor: GOLD, transform: isActive(item) ? "scaleX(1)" : "scaleX(0)" }} />
                </Link>
              ) : (
                <button
                  onClick={() => setOpenMenu(openMenu === item.label ? null : item.label)}
                  aria-expanded={openMenu === item.label}
                  aria-haspopup="true"
                  className="relative flex items-center gap-1 px-3.5 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
                  data-testid={`biz-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                  <span className="absolute bottom-0.5 left-3.5 right-3.5 h-[1.5px] origin-left transition-transform duration-300"
                        style={{ backgroundColor: GOLD, transform: isActive(item) ? "scaleX(1)" : "scaleX(0)" }} />
                </button>
              )}
              {item.children && openMenu === item.label && (
                <div className="absolute left-0 top-full w-72 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50"
                     data-testid="biz-nav-dropdown">
                  {item.children.map((c) => (
                    <Link key={c.to} to={c.to} onClick={go}
                      className={`block text-left px-4 py-2.5 text-sm transition-colors hover:bg-slate-50 ${
                        pathname === c.to ? "text-slate-900 font-semibold" : "text-slate-600"
                      }`}
                      data-testid={`biz-nav-sub-${c.to.split("/").pop()}`}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {setLang && languages.length > 1 && (
            <div className="relative">
              <button onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 px-2.5 py-2 rounded-full border border-slate-200 hover:bg-slate-50 text-sm transition-colors"
                data-testid="business-lang-switcher" aria-label="Skift sprog"
                aria-expanded={langOpen} aria-haspopup="true">
                <Globe className="h-4 w-4 text-slate-500" />
                <span className="text-xs uppercase font-semibold text-slate-700">{current?.code}</span>
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-xl border border-slate-200 py-1 z-50">
                    {languages.map((l) => (
                      <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-slate-50 ${lang === l.code ? "bg-amber-50" : ""}`}
                        data-testid={`business-lang-${l.code}`}>
                        <span className="flex items-center gap-2"><span>{l.flag}</span><span className="text-slate-700">{l.label}</span></span>
                        {lang === l.code && <Check className="h-4 w-4" style={{ color: GOLD }} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <Link to="/mitvotra"
            className="hidden md:inline-flex text-sm px-4 py-2 rounded-full font-medium text-slate-700 border border-slate-200 hover:border-slate-400 transition-colors whitespace-nowrap"
            data-testid="biz-portal-login-btn">
            Log ind
          </Link>
          <Link to="/vognmand-onboarding" onClick={go} title="Lav din vognmandsplan"
            className="hidden sm:inline-flex items-center text-sm px-5 py-2.5 rounded-full font-semibold whitespace-nowrap transition-all hover:shadow-lg hover:-translate-y-px"
            style={{ backgroundColor: GOLD, color: NAVY }} data-testid="biz-header-cta">
            Beregn din pris
          </Link>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            aria-label={mobileOpen ? "Luk menu" : "Åbn menu"} aria-expanded={mobileOpen} aria-controls="biz-mobile-nav"
            data-testid="biz-mobile-menu-btn">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="lg:hidden absolute left-0 right-0 top-full bg-white border-b border-slate-200 shadow-xl z-50 max-h-[80vh] overflow-y-auto"
             id="biz-mobile-nav" aria-label="Votra Business mobilmenu"
             data-testid="biz-mobile-nav">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((item) => item.children ? (
              <div key={item.label}>
                <button onClick={() => setMobileSub(mobileSub === item.label ? null : item.label)}
                  aria-expanded={mobileSub === item.label}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-slate-800 hover:bg-slate-50">
                  {item.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileSub === item.label ? "rotate-180" : ""}`} />
                </button>
                {mobileSub === item.label && (
                  <div className="pl-4">
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to} onClick={go}
                        className="block text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link key={item.label} to={item.to} onClick={go}
                className="px-4 py-3 rounded-lg text-sm font-medium text-left text-slate-800 hover:bg-slate-50">
                {item.label}
              </Link>
            ))}
            <Link to="/mitvotra" onClick={go}
              className="px-4 py-3 rounded-lg text-sm font-medium text-left text-slate-600 hover:bg-slate-50">
              Log ind i portalen
            </Link>
            <Link to="/vognmand-onboarding" onClick={go}
              className="mt-2 px-4 py-3 rounded-full text-sm font-semibold text-center"
              style={{ backgroundColor: GOLD, color: NAVY }} data-testid="biz-mobile-cta">
              Beregn din pris
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default BusinessHeader;
